import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { z } from 'zod';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import { supabase } from '~/services/supabase';
import { runAIFormBuilder } from '~/services/aiFormBuilder';
import type { IFormSchema } from '~/types';
import { ZFormSchema } from '~/types';
import type { AIStep } from '~/services/aiFormBuilder';
import { PreviewIframe } from './PreviewIframe';
import { ShareModal } from './ShareModal';

export const FormEditorNew = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  const [formName, setFormName] = useState('');
  const [schema, setSchema] = useState<string>('');
  const [parsedSchema, setParsedSchema] = useState<IFormSchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Side panel state
  const [activeTab, setActiveTab] = useState<'ai' | 'schema'>('ai');
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [panelWidth, setPanelWidth] = useState(500);
  const isResizingRef = useRef(false);

  const MIN_PANEL_WIDTH = 500;
  const MAX_PANEL_WIDTH = 900;

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      const startX = e.clientX;
      const startWidth = panelWidth;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizingRef.current) return;
        // Dragging left increases width, dragging right decreases
        const delta = startX - moveEvent.clientX;
        const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidth + delta));
        setPanelWidth(newWidth);
      };

      const onMouseUp = () => {
        isResizingRef.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [panelWidth],
  );

  // AI state
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [, setAiStep] = useState<AIStep>('context_gathering');

  // Load form data
  useEffect(() => {
    setHasUnsavedChanges(false);
    if (id && id !== 'new') {
      loadForm(id);
    } else {
      // New form - set default schema
      isLoadingRef.current = true;
      const defaultSchema: IFormSchema = {
        title: 'New Form',
        type: 'object',
        properties: {
          email: {
            type: 'string',
            format: 'email',
            title: 'Email Address',
          },
        },
        required: ['email'],
      };
      setSchema(JSON.stringify(defaultSchema, null, 2));
      setParsedSchema(defaultSchema);
      setFormName('New Form');
      requestAnimationFrame(() => {
        isLoadingRef.current = false;
      });
    }
  }, [id]);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages, aiLoading]);

  const loadForm = async (formId: string) => {
    isLoadingRef.current = true;
    const { data, error } = await supabase.from('forms').select('*').eq('id', formId).single();

    if (error || !data) {
      console.error('Error loading form:', error);
      isLoadingRef.current = false;
      return;
    }

    setFormName(data.name);
    setSchema(JSON.stringify(data.json_schema, null, 2));
    setParsedSchema(data.json_schema);
    // Allow Monaco to process the value change before re-enabling change tracking
    requestAnimationFrame(() => {
      isLoadingRef.current = false;
    });
  };

  // Keep a ref to the latest schema text so the blur handler avoids stale closures
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  /** Parse + validate schema text; returns true if valid */
  const validateAndSetSchema = useCallback((value: string) => {
    try {
      const parsed = JSON.parse(value);
      const validated = ZFormSchema.parse(parsed);
      setParsedSchema(validated);
      setSchemaError(null);
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setSchemaError(
          err.issues
            .map((e: { path: PropertyKey[]; message: string }) => `${e.path.join('.')}: ${e.message}`)
            .join('\n'),
        );
      } else if (err instanceof SyntaxError) {
        setSchemaError('Invalid JSON syntax');
      } else {
        setSchemaError('Invalid schema');
      }
      setParsedSchema(null);
      return false;
    }
  }, []);

  /** Keystroke handler — only updates raw text + eager error clearance */
  const handleSchemaChange = (value: string | undefined) => {
    if (!value) return;
    setSchema(value);
    if (!isLoadingRef.current) {
      setHasUnsavedChanges(true);
    }

    // Eager clearance: if an error is currently displayed, try to clear it
    if (schemaError) {
      try {
        const parsed = JSON.parse(value);
        const validated = ZFormSchema.parse(parsed);
        // Valid again — clear error and update preview immediately
        setParsedSchema(validated);
        setSchemaError(null);
      } catch {
        // Still invalid — keep the existing error text, don't update it mid-typing
      }
    }
    // If no error is showing, skip validation entirely (preview updates on blur)
  };

  /** Blur handler — full validation, called when user leaves the editor */
  const handleEditorMount = useCallback(
    (editor: { onDidBlurEditorText: (listener: () => void) => void }) => {
      editor.onDidBlurEditorText(() => {
        validateAndSetSchema(schemaRef.current);
      });
    },
    [validateAndSetSchema],
  );

  const handleAISubmit = async () => {
    if (!aiInput.trim()) return;

    setAiLoading(true);
    const userMessage = { role: 'user' as const, content: aiInput };
    setAiMessages((prev) => [...prev, userMessage]);
    const userInput = aiInput;
    setAiInput('');

    try {
      // Always pass current schema to AI — let intent classifier decide routing
      const generatedSchema = await runAIFormBuilder(
        userInput,
        aiMessages,
        (message, step) => {
          setAiStep(step);
          if (message) {
            setAiMessages((prev) => [...prev, { role: 'assistant', content: message }]);
          }
        },
        parsedSchema,
      );

      // Only set schema in state if validation passed (runAIFormBuilder only returns valid schemas)
      setSchema(JSON.stringify(generatedSchema, null, 2));
      setParsedSchema(generatedSchema);
      setSchemaError(null);
      setHasUnsavedChanges(true);

      // Success message — cosmetic distinction based on saved form vs new
      const isExistingForm = id && id !== 'new';
      const successMessage = isExistingForm
        ? "Done! I've updated your form. You can see the changes in the preview on the left."
        : "Perfect! I've generated your form. You can see the preview on the left and edit the schema using the Schema Editor tab if needed.";
      setAiMessages((prev) => [...prev, { role: 'assistant', content: successMessage }]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate form';

      if (errorMessage.startsWith('NEED_CLARIFICATION:')) {
        // Need more info from user - don't update schema
        const question = errorMessage.replace('NEED_CLARIFICATION:', '').trim();
        setAiMessages((prev) => [...prev, { role: 'assistant', content: question }]);
      } else {
        // Error occurred - don't update schema
        setAiMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${errorMessage}` }]);
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!parsedSchema) {
      toast.error('Please fix schema errors before saving');
      return;
    }

    setIsSaving(true);

    try {
      if (id && id !== 'new') {
        // Update existing
        const { error } = await supabase
          .from('forms')
          .update({ name: formName, json_schema: parsedSchema })
          .eq('id', id);
        if (error) throw error;
      } else {
        // Create new
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userEmail = session?.user?.email ?? 'unknown';
        const { data, error } = await supabase
          .from('forms')
          .insert({ name: formName, json_schema: parsedSchema, status: 'draft', created_by: userEmail })
          .select()
          .single();
        if (error) throw error;
        if (data) {
          navigate(`/forms/${data.id}/edit`);
        }
      }

      setHasUnsavedChanges(false);
      toast.success('Draft saved');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Discard and leave?')) {
        return;
      }
    }
    navigate('/forms');
  };

  const handlePublish = async () => {
    if (!parsedSchema) {
      toast.error('Please fix schema errors before publishing');
      return;
    }

    setIsSaving(true);

    try {
      await supabase.from('forms').update({ status: 'published', published_at: new Date().toISOString() }).eq('id', id);

      toast.success('Form published!');
      setShowShareModal(true);
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Failed to publish');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Top Bar with Save/Publish */}
      <div className="h-16 border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleCancel}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            title="Back to forms"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path
                fillRule="evenodd"
                d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <input
            type="text"
            value={formName}
            onChange={(e) => {
              setFormName(e.target.value);
              setHasUnsavedChanges(true);
            }}
            className="text-lg font-medium border-none outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 py-1"
            placeholder="Form Name"
          />
        </div>
        <div className="flex items-center space-x-2">
          {id && id !== 'new' && (
            <button
              onClick={() => setShowShareModal(true)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-1.5"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M13 4.5a2.5 2.5 0 11.702 1.737L6.97 9.604a2.518 2.518 0 010 .792l6.733 3.367a2.5 2.5 0 11-.671 1.341l-6.733-3.367a2.5 2.5 0 110-3.474l6.733-3.367A2.52 2.52 0 0113 4.5z" />
              </svg>
              Share
            </button>
          )}
          {id === 'new' || hasUnsavedChanges ? (
            <button
              onClick={handleSaveDraft}
              disabled={isSaving || !!schemaError}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Draft'}
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={isSaving || !!schemaError}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Publishing...' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content: Live Preview + Right Panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Live Preview (Center) */}
        <div className="flex-1 p-8 overflow-auto bg-gray-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
              Live Preview
            </h2>
            <div className="bg-white rounded-lg shadow-sm p-8">
              <PreviewIframe schema={parsedSchema} schemaError={schemaError} />
            </div>
          </div>
        </div>

        {/* Right Side Panel */}
        <div
          className={`${isPanelOpen ? '' : 'w-0'} border-l border-gray-200 bg-white flex flex-col flex-shrink-0 relative`}
          style={isPanelOpen ? { width: panelWidth } : undefined}
        >
          {isPanelOpen && (
            <>
              {/* Resize handle */}
              <div
                onMouseDown={handleResizeStart}
                className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary-400/50 active:bg-primary-400/50 z-10"
              />
              {/* Panel Tabs */}
              <div className="h-12 border-b border-gray-200 flex flex-shrink-0">
                <button
                  onClick={() => setActiveTab('ai')}
                  className={`flex-1 text-sm font-medium ${
                    activeTab === 'ai'
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  AI Assistant
                </button>
                <button
                  onClick={() => setActiveTab('schema')}
                  className={`flex-1 text-sm font-medium ${
                    activeTab === 'schema'
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Schema Editor
                </button>
              </div>

              {/* Panel Content */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {activeTab === 'ai' ? (
                  /* AI Chat Panel */
                  <>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {aiMessages.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                          {id && id !== 'new' && parsedSchema ? (
                            <>
                              <p className="mb-2">Edit your form with AI</p>
                              <p className="text-sm">
                                Examples: {'"'}Add a phone number field{'"'}, {'"'}Make email optional{'"'}, {'"'}Change
                                the title{'"'}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="mb-2">Describe the form you want to build</p>
                              <p className="text-sm">
                                Example: {'"'}Create a contact form with email, name, and company fields{'"'}
                              </p>
                            </>
                          )}
                        </div>
                      ) : (
                        aiMessages.map((msg, idx) => (
                          <div key={idx} className={`${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                            <div
                              className={`inline-block max-w-[85%] rounded-lg px-4 py-2 ${
                                msg.role === 'user' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'
                              }`}
                            >
                              {msg.role === 'assistant' ? (
                                <div className="prose prose-sm max-w-none">
                                  <ReactMarkdown
                                    components={{
                                      ol: (props) => <ol className="list-decimal ml-4 space-y-2" {...props} />,
                                      ul: (props) => <ul className="list-disc ml-4 space-y-1" {...props} />,
                                      li: (props) => <li className="ml-0" {...props} />,
                                      p: (props) => <p className="mb-2 last:mb-0" {...props} />,
                                    }}
                                  >
                                    {msg.content}
                                  </ReactMarkdown>
                                </div>
                              ) : (
                                msg.content
                              )}
                            </div>
                          </div>
                        ))
                      )}
                      {aiLoading && (
                        <div className="text-left">
                          <div className="inline-block bg-gray-100 rounded-lg px-4 py-2">
                            <div className="text-gray-400 animate-pulse">Thinking...</div>
                          </div>
                        </div>
                      )}
                      <div ref={chatMessagesEndRef} />
                    </div>
                    <div className="flex-shrink-0 border-t border-gray-200 p-4">
                      <div className="relative">
                        <textarea
                          value={aiInput}
                          onChange={(e) => setAiInput(e.target.value)}
                          onKeyDown={(e) => {
                            // Enter to submit, Cmd+Enter or Ctrl+Enter for new line
                            if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !aiLoading) {
                              e.preventDefault();
                              handleAISubmit();
                            }
                          }}
                          placeholder="Describe your form... (⌘+Enter for new line)"
                          className="w-full pl-3 pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none min-h-[44px] max-h-[200px]"
                          disabled={aiLoading}
                          rows={1}
                          style={{ height: 'auto' }}
                          onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            target.style.height = 'auto';
                            target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
                          }}
                        />
                        <button
                          onClick={handleAISubmit}
                          disabled={aiLoading || !aiInput.trim()}
                          className="absolute bottom-2 right-2 p-2 text-primary-600 hover:text-primary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Send (Enter)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                          >
                            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Schema Editor Panel */
                  <div className="h-full flex flex-col">
                    {schemaError && (
                      <div className="px-3 py-2 bg-red-50 border-b border-red-200 text-red-700 text-xs font-mono whitespace-pre-wrap">
                        {schemaError}
                      </div>
                    )}
                    <div className={`flex-1 ${schemaError ? 'ring-2 ring-red-400' : ''}`}>
                      <Editor
                        height="100%"
                        defaultLanguage="json"
                        value={schema}
                        onChange={handleSchemaChange}
                        onMount={handleEditorMount}
                        theme="vs-light"
                        options={{
                          minimap: { enabled: false },
                          fontSize: 13,
                          lineNumbers: 'on',
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Panel Toggle Button */}
        <button
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white border border-gray-300 rounded-l-md px-1 py-4 hover:bg-gray-50"
        >
          {isPanelOpen ? '→' : '←'}
        </button>
      </div>

      {showShareModal && id && id !== 'new' && <ShareModal formId={id} onClose={() => setShowShareModal(false)} />}
    </div>
  );
};
